import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TL ile işlem gören kategoriler
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık kategorisine göre para birimi belirle
const getAssetCurrency = (asset) => {
  if (!asset) return 'TRY';
  if (asset.currency) return asset.currency;
  return TL_CATEGORIES.includes(asset.category) ? 'TRY' : 'USD';
};

// Fiyat için para birimi simgesi (varlığın kendi para birimi)
const getPriceSymbol = (asset) => {
  const currency = getAssetCurrency(asset);
  return currency === 'USD' ? '$' : '₺';
};

function UserDetailManagement({ user, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('info');
  const [positions, setPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  
  // Aktivite Logları
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  
  // User Info Edit
  const [isEditingUserInfo, setIsEditingUserInfo] = useState(false);
  const [userInfoForm, setUserInfoForm] = useState({
    name: '',
    surname: '',
    tc_no: '',
    phone: '',
    email: '',
    account_type: 'STANDART'
  });
  
  // Forms
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [showAddClosed, setShowAddClosed] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingClosed, setEditingClosed] = useState(null);
  const [currentPrices, setCurrentPrices] = useState({});
  
  const [positionForm, setPositionForm] = useState({
    asset_id: '',
    amount: '',
    avg_buy_price: '',
    leverage: '1',
    stop_loss: '',
    take_profit: ''
  });
  
  // Arama state'i
  const [assetSearch, setAssetSearch] = useState('');
  
  // Kar/Zarar önizleme hesaplama
  const calculatePnLPreview = () => {
    if (!positionForm.asset_id || !positionForm.amount || !positionForm.avg_buy_price) return null;
    
    const amount = parseFloat(positionForm.amount);
    const entryPrice = parseFloat(positionForm.avg_buy_price);
    const leverage = parseInt(positionForm.leverage) || 1;
    
    // Güncel fiyatı ve gizli kaldıracı bul
    const asset = assets.find(a => a.id === positionForm.asset_id);
    const currentPrice = currentPrices[positionForm.asset_id] || asset?.current_price || entryPrice;
    
    // ⚠️ GİZLİ KALDIRAC: Asset'ten veya mevcut pozisyondan al
    const hiddenLeverage = asset?.hidden_leverage || editingPosition?.hidden_leverage || 1;
    
    // USD/TRY kuru (varsayılan)
    const usdTryRate = 42.50;
    
    // Varlık USD mi?
    const assetCurrency = getAssetCurrency(asset);
    const isUsd = assetCurrency === 'USD';
    
    // Kar/Zarar hesapla - GİZLİ KALDIRAC DAHİL
    const priceDiff = currentPrice - entryPrice;
    let pnl = priceDiff * amount * leverage;
    let pnlPercent = entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 * leverage : 0;
    
    // USD varlıklar için TL'ye çevir
    if (isUsd) {
      pnl = pnl * usdTryRate;
    }
    
    // Gizli kaldıracı uygula
    if (hiddenLeverage > 1) {
      pnl *= hiddenLeverage;
      pnlPercent *= hiddenLeverage;
      console.log(`🔒 Gizli Kaldıraç Preview: ${hiddenLeverage}x uygulandı, PnL: ${pnl.toFixed(2)} TL`);
    }
    
    // %10 SINIRI SADECE BIST_100 için geçerli
    const category = asset?.category || '';
    if (category === 'BIST_100') {
      if (pnlPercent > 10) {
        pnlPercent = 10;
      } else if (pnlPercent < -10) {
        pnlPercent = -10;
      }
    }
    
    // Toplam değer TL cinsinden
    const totalValue = isUsd ? (currentPrice * amount * usdTryRate) : (currentPrice * amount);
    
    return {
      currentPrice,
      pnl,  // Her zaman TL cinsinden
      pnlPercent,
      totalValue,  // Her zaman TL cinsinden
      hiddenLeverage,  // Badge için
      isUsd  // USD varlık mı
    };
  };
  
  const [closedForm, setClosedForm] = useState({
    asset_id: '',
    amount: '',
    entry_price: '',
    exit_price: '',
    profit_loss: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  
  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };
  
  useEffect(() => {
    // User değiştiğinde verileri yeniden çek
    if (user?.id) {
      console.log('📊 UserDetailManagement: Loading data for user:', user.id, user.email);
      fetchData();
      fetchAssets();
      // Form'u da güncelle
      setUserInfoForm({
        name: user?.name || '',
        surname: user?.surname || '',
        tc_no: user?.tc_no || '',
        phone: user?.phone || '',
        email: user?.email || '',
        account_type: user?.account_type || 'STANDART'
      });
    }
  }, [user?.id]); // user.id değiştiğinde yeniden çalış
  
  // Aktivite logları sekmesi açılınca verileri çek
  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [activeTab]);
  
  // Auto-calculate profit/loss when entry_price, exit_price, or amount changes
  useEffect(() => {
    const { entry_price, exit_price, amount } = closedForm;
    
    // Only calculate if all three values are present and valid
    if (entry_price && exit_price && amount) {
      const entryPrice = parseFloat(entry_price);
      const exitPrice = parseFloat(exit_price);
      const qty = parseFloat(amount);
      
      if (!isNaN(entryPrice) && !isNaN(exitPrice) && !isNaN(qty)) {
        const calculatedPnL = (exitPrice - entryPrice) * qty;
        setClosedForm(prev => ({
          ...prev,
          profit_loss: calculatedPnL.toFixed(2)
        }));
      }
    }
  }, [closedForm.entry_price, closedForm.exit_price, closedForm.amount]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [posRes, txRes] = await Promise.all([
        axios.get(`${API}/admin/user-positions/${user.id}`, axiosConfig),
        axios.get(`${API}/admin/user-transactions/${user.id}`, axiosConfig)
      ]);
      setPositions(posRes.data || []);
      setTransactions(txRes.data || []);
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };
  
  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API}/assets`, axiosConfig);
      console.log('✅ UserDetailManagement: Assets fetched:', res.data?.length, 'assets');
      setAssets(res.data || []);
      
      // Güncel fiyatları kaydet
      const prices = {};
      (res.data || []).forEach(asset => {
        prices[asset.id] = asset.current_price;
      });
      setCurrentPrices(prices);
    } catch (err) {
      console.error('❌ UserDetailManagement: Assets fetch error:', err);
    }
  };
  
  // Aktivite loglarını getir
  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user-activity-logs/${user.id}?limit=100`, axiosConfig);
      setActivityLogs(res.data || []);
    } catch (err) {
      console.error('❌ Activity logs fetch error:', err);
      setActivityLogs([]);
    }
    setActivityLoading(false);
  };
  
  // Update user info
  const handleUpdateUserInfo = async () => {
    try {
      await axios.put(`${API}/admin/users/${user.id}/update`, userInfoForm, axiosConfig);
      showAlert('Kullanıcı bilgileri güncellendi!', 'success');
      setIsEditingUserInfo(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Add position
  const handleAddPosition = async () => {
    if (!positionForm.asset_id || !positionForm.amount || !positionForm.avg_buy_price) {
      showAlert('Varlık, miktar ve fiyat zorunlu!', 'error');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/add-position`, {
        user_id: user.id,
        asset_id: positionForm.asset_id,
        amount: parseFloat(positionForm.amount),
        avg_buy_price: parseFloat(positionForm.avg_buy_price),
        leverage: parseInt(positionForm.leverage),
        stop_loss: positionForm.stop_loss ? parseFloat(positionForm.stop_loss) : null,
        take_profit: positionForm.take_profit ? parseFloat(positionForm.take_profit) : null
      }, axiosConfig);
      
      showAlert('Pozisyon eklendi!', 'success');
      setShowAddPosition(false);
      setPositionForm({ asset_id: '', amount: '', avg_buy_price: '', leverage: '1', stop_loss: '', take_profit: '' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Edit position
  const handleEditPosition = async () => {
    if (!editingPosition) return;
    
    try {
      await axios.put(`${API}/admin/update-position`, {
        user_id: user.id,
        asset_id: editingPosition.asset_id,
        amount: parseFloat(positionForm.amount),
        avg_buy_price: parseFloat(positionForm.avg_buy_price),
        leverage: parseInt(positionForm.leverage),
        stop_loss: positionForm.stop_loss ? parseFloat(positionForm.stop_loss) : null,
        take_profit: positionForm.take_profit ? parseFloat(positionForm.take_profit) : null
      }, axiosConfig);
      
      showAlert('Pozisyon güncellendi!', 'success');
      setEditingPosition(null);
      setPositionForm({ asset_id: '', amount: '', avg_buy_price: '', leverage: '1', stop_loss: '', take_profit: '' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Delete position
  const handleDeletePosition = async (positionId, assetSymbol) => {
    
    try {
      await axios.delete(`${API}/admin/delete-position/${user.id}/${positionId}`, axiosConfig);
      showAlert('Pozisyon silindi!', 'success');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Add closed transaction
  const handleAddClosed = async () => {
    if (!closedForm.asset_id || !closedForm.amount || !closedForm.profit_loss) {
      showAlert('Varlık, Miktar ve Kar/Zarar alanları zorunludur', 'error');
      return;
    }

    try {
      if (editingClosed) {
        // DÜZENLEME
        await axios.put(`${API}/admin/update-completed-transaction`, {
          transaction_id: editingClosed.id,
          user_id: user.id,
          asset_id: closedForm.asset_id,
          amount: parseFloat(closedForm.amount),
          entry_price: parseFloat(closedForm.entry_price) || 0,
          exit_price: parseFloat(closedForm.exit_price) || 0,
          profit_loss: parseFloat(closedForm.profit_loss),
          entry_date: closedForm.entry_date || null,
          old_profit_loss: editingClosed.profit_loss
        }, axiosConfig);

        showAlert('Tamamlanmış işlem güncellendi', 'success');
      } else {
        // YENİ EKLEME
        await axios.post(`${API}/admin/add-completed-transaction`, {
          user_id: user.id,
          asset_id: closedForm.asset_id,
          amount: parseFloat(closedForm.amount),
          entry_price: parseFloat(closedForm.entry_price) || 0,
          exit_price: parseFloat(closedForm.exit_price) || 0,
          profit_loss: parseFloat(closedForm.profit_loss),
          entry_date: closedForm.entry_date || null
        }, axiosConfig);

        showAlert('Tamamlanmış işlem eklendi', 'success');
      }

      setClosedForm({ asset_id: '', amount: '', entry_price: '', exit_price: '', profit_loss: '', entry_date: '' });
      setShowAddClosed(false);
      setEditingClosed(null);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDeleteClosed = async (txId) => {

    try {
      await axios.delete(`${API}/admin/delete-completed-transaction/${user.id}/${txId}`, axiosConfig);
      showAlert('İşlem silindi', 'success');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Update password
  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showAlert('Şifre en az 6 karakter olmalı!', 'error');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${user.id}/password`, 
        { new_password: newPassword }, 
        axiosConfig
      );
      showAlert('Şifre güncellendi!', 'success');
      setNewPassword('');
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  const openPositions = positions.filter(p => p.amount > 0);
  const closedTxs = transactions.filter(tx => 
    tx.transaction_type && (
      tx.transaction_type.includes('CLOSE') || 
      tx.transaction_type === 'SELL'
    )
  );
  
  const totalOpenPnL = openPositions.reduce((sum, p) => sum + (p.profit_loss || 0), 0);
  const totalClosedPnL = closedTxs.reduce((sum, tx) => sum + (tx.profit_loss || 0), 0);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-gray-800 rounded-none sm:rounded-lg w-full h-full sm:max-w-7xl sm:h-auto sm:max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-3 sm:p-6 flex justify-between items-center z-10">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold truncate">👤 {user.email}</h2>
            <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
              <span className="text-yellow-400 font-mono">ID: {user.id}</span>
              <span className="text-gray-400">•</span>
              <span className="text-green-400">Bakiye: ₺{user.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl font-bold ml-2 flex-shrink-0">×</button>
        </div>
        
        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-700 px-2 sm:px-6 scrollbar-hide">
          <button onClick={() => setActiveTab('info')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'info' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📋 Bilgiler</button>
          <button onClick={() => setActiveTab('positions')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'positions' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📈 Açık Pozisyonlar ({openPositions.length})</button>
          <button onClick={() => setActiveTab('closed')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'closed' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>✅ Tamamlananlar ({closedTxs.length})</button>
          <button onClick={() => setActiveTab('credit')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'credit' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>💳 Kredi</button>
          <button onClick={() => setActiveTab('password')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'password' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>🔐 Şifre</button>
          <button onClick={() => setActiveTab('activity')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'activity' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📜 Aktivite Logları</button>
        </div>
        
        {/* Content */}
        <div className="p-3 sm:p-6">
          {activeTab === 'info' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-bold">Kullanıcı Bilgileri</h3>
                  <button 
                    onClick={() => {
                      if (isEditingUserInfo) {
                        setIsEditingUserInfo(false);
                      } else {
                        setUserInfoForm({
                          name: user?.name || '',
                          surname: user?.surname || '',
                          tc_no: user?.tc_no || '',
                          phone: user?.phone || '',
                          email: user?.email || '',
                          account_type: user?.account_type || 'STANDART'
                        });
                        setIsEditingUserInfo(true);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isEditingUserInfo ? 'bg-gray-600 hover:bg-gray-500' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {isEditingUserInfo ? '❌ İptal' : '✏️ Düzenle'}
                  </button>
                </div>
                
                {isEditingUserInfo ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Ad</label>
                        <input
                          type="text"
                          value={userInfoForm.name}
                          onChange={(e) => setUserInfoForm({...userInfoForm, name: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Soyad</label>
                        <input
                          type="text"
                          value={userInfoForm.surname}
                          onChange={(e) => setUserInfoForm({...userInfoForm, surname: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Email</label>
                        <input
                          type="email"
                          value={userInfoForm.email}
                          onChange={(e) => setUserInfoForm({...userInfoForm, email: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">📱 Telefon</label>
                        <input
                          type="text"
                          value={userInfoForm.phone}
                          onChange={(e) => setUserInfoForm({...userInfoForm, phone: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                          placeholder="5XX XXX XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">🆔 TC Kimlik No</label>
                        <input
                          type="text"
                          value={userInfoForm.tc_no}
                          onChange={(e) => setUserInfoForm({...userInfoForm, tc_no: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                          maxLength={11}
                          placeholder="TC Kimlik Numarası"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Hesap Tipi</label>
                        <select
                          value={userInfoForm.account_type}
                          onChange={(e) => setUserInfoForm({...userInfoForm, account_type: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="STANDART">Standart</option>
                          <option value="VIP">VIP</option>
                          <option value="KOMISYONSUZ">Komisyonsuz</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleUpdateUserInfo}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium"
                      >
                        💾 Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div><div className="text-sm text-gray-400">Email</div><div className="font-semibold">{user.email}</div></div>
                    <div><div className="text-sm text-gray-400">Ad Soyad</div><div className="font-semibold">{user.full_name || user.name || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">📱 Telefon</div><div className="font-semibold">{user.phone || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">🆔 TC No</div><div className="font-semibold">{user.tc_no || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">Bakiye</div><div className="font-semibold text-green-400">₺{user.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div></div>
                    <div><div className="text-sm text-gray-400">Hesap Tipi</div><div className="font-semibold">{user.account_type || 'STANDART'}</div></div>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">İstatistikler</h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold">{transactions.length}</div>
                    <div className="text-xs sm:text-sm text-gray-400">Toplam İşlem</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold">{openPositions.length}</div>
                    <div className="text-xs sm:text-sm text-gray-400">Açık Pozisyon</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className={`text-lg sm:text-2xl font-bold ${(totalOpenPnL + totalClosedPnL) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {(totalOpenPnL + totalClosedPnL) >= 0 ? '+' : ''}₺{(totalOpenPnL + totalClosedPnL).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400">Toplam Kar/Zarar</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'positions' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-bold">Açık Pozisyonlar</h3>
                <button onClick={() => { setShowAddPosition(!showAddPosition); setEditingPosition(null); }} className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-sm sm:text-base whitespace-nowrap">
                  {showAddPosition ? '❌ İptal' : '➕ Ekle'}
                </button>
              </div>
              
              {(showAddPosition || editingPosition) && (
                <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                  <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">{editingPosition ? '📝 Pozisyon Düzenle' : '✨ Yeni Pozisyon Ekle'}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">🔍 Varlık Ara ve Seç *</label>
                      {!editingPosition && (
                        <input
                          type="text"
                          value={assetSearch}
                          onChange={(e) => setAssetSearch(e.target.value)}
                          className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base mb-2"
                          placeholder="Hisse adı yazın... (örn: GARAN, BTC, GOLD, THYAO)"
                        />
                      )}
                      <select 
                        value={positionForm.asset_id} 
                        onChange={(e) => {
                          const selectedAsset = assets.find(a => a.id === e.target.value);
                          setPositionForm({
                            ...positionForm, 
                            asset_id: e.target.value,
                            avg_buy_price: selectedAsset?.current_price?.toFixed(2) || ''
                          });
                        }} 
                        className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" 
                        disabled={!!editingPosition}
                        size={editingPosition ? 1 : 6}
                      >
                        <option value="">-- Varlık Seçin --</option>
                        {assets
                          .filter(a => 
                            assetSearch === '' || 
                            a.symbol?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.name?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.category?.toLowerCase().includes(assetSearch.toLowerCase())
                          )
                          .slice(0, 50)
                          .map(a => (
                            <option key={a.id} value={a.id}>
                              {a.symbol} ({a.category}) - ₺{(a.current_price || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </option>
                          ))
                        }
                      </select>
                      {assetSearch && !editingPosition && (
                        <p className="text-xs text-gray-400 mt-1">
                          {assets.filter(a => 
                            a.symbol?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.name?.toLowerCase().includes(assetSearch.toLowerCase())
                          ).length} sonuç bulundu
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Miktar *</label>
                      <input type="number" value={positionForm.amount} onChange={(e) => setPositionForm({...positionForm, amount: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="100" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Alış Fiyatı *</label>
                      <input type="number" step="0.01" value={positionForm.avg_buy_price} onChange={(e) => setPositionForm({...positionForm, avg_buy_price: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="185.50" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Kaldıraç</label>
                      <select value={positionForm.leverage} onChange={(e) => setPositionForm({...positionForm, leverage: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base">
                        {[1,2,5,10,20,50,100,200].map(l => (<option key={l} value={l}>{l}x</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Stop Loss ({getCurrencySymbol(user)}) (opsiyonel)</label>
                      <input type="number" step="0.01" value={positionForm.stop_loss} onChange={(e) => setPositionForm({...positionForm, stop_loss: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="180.00" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Take Profit ({getCurrencySymbol(user)}) (opsiyonel)</label>
                      <input type="number" step="0.01" value={positionForm.take_profit} onChange={(e) => setPositionForm({...positionForm, take_profit: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="200.00" />
                    </div>
                  </div>
                  
                  {/* Fiyat ve Kar/Zarar Önizleme Kutucuğu */}
                  {positionForm.asset_id && positionForm.amount && positionForm.avg_buy_price && (() => {
                    const selectedAsset = assets.find(a => a.id === positionForm.asset_id);
                    const priceSymbol = getPriceSymbol(selectedAsset); // Varlığın kendi para birimi ($ veya ₺)
                    const isUsdAsset = getAssetCurrency(selectedAsset) === 'USD';
                    return (
                    <div className="mt-4 bg-blue-900/30 border border-blue-500 rounded-lg p-4">
                      <h5 className="text-blue-400 font-semibold mb-2 text-sm">📊 İşlem Önizleme</h5>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-400">Varlık:</span>
                          <span className="text-white ml-2">{selectedAsset?.symbol || '-'}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Miktar:</span>
                          <span className="text-white ml-2">{positionForm.amount}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Giriş Fiyatı:</span>
                          <span className="text-white ml-2">{priceSymbol}{parseFloat(positionForm.avg_buy_price).toLocaleString(isUsdAsset ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2})}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Kaldıraç:</span>
                          <span className="text-white ml-2">{positionForm.leverage}x</span>
                        </div>
                        
                        {/* Güncel Fiyat ve Kar/Zarar Önizleme */}
                        {(() => {
                          const preview = calculatePnLPreview();
                          if (!preview) return null;
                          return (
                            <>
                              <div>
                                <span className="text-gray-400">Güncel Fiyat:</span>
                                <span className="text-cyan-400 ml-2">{priceSymbol}{preview.currentPrice.toLocaleString(isUsdAsset ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2})}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">Toplam Değer:</span>
                                <span className="text-white ml-2">₺{preview.totalValue.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                              </div>
                              {/* GİZLİ KALDIRAC BADGE - SADECE ADMİN GÖRÜR */}
                              {preview.hiddenLeverage > 1 && (
                                <div className="col-span-2 py-1">
                                  <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-bold">
                                    🔒 Gizli Kaldıraç: {preview.hiddenLeverage}x
                                  </span>
                                </div>
                              )}
                              <div className="col-span-2 pt-2 border-t border-blue-500/30 mt-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-400 font-semibold">💰 Tahmini Kar/Zarar:</span>
                                  <div className="text-right">
                                    <span className={`font-bold text-lg ${preview.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {preview.pnl >= 0 ? '+' : ''}₺{Math.abs(preview.pnl).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                                    </span>
                                    <span className={`ml-2 text-sm ${preview.pnlPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      ({preview.pnlPercent >= 0 ? '+' : ''}{preview.pnlPercent.toFixed(2)}%)
                                    </span>
                                    {preview.hiddenLeverage > 1 && (
                                      <span className="ml-1 text-yellow-400 text-xs">(🔒{preview.hiddenLeverage}x)</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                        
                        <div className="col-span-2 pt-2 border-t border-blue-500/30">
                          <span className="text-gray-400">Toplam İşlem Tutarı:</span>
                          <span className="text-green-400 font-bold text-lg ml-2">
                            ₺{(parseFloat(positionForm.amount) * parseFloat(positionForm.avg_buy_price)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                          </span>
                        </div>
                        {parseInt(positionForm.leverage) > 1 && (
                          <div className="col-span-2">
                            <span className="text-gray-400">Gerekli Teminat:</span>
                            <span className="text-yellow-400 font-bold ml-2">
                              ₺{((parseFloat(positionForm.amount) * parseFloat(positionForm.avg_buy_price)) / parseInt(positionForm.leverage)).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                            </span>
                          </div>
                        )}
                        {positionForm.stop_loss && (
                          <div>
                            <span className="text-gray-400">Stop Loss:</span>
                            <span className="text-red-400 ml-2">{priceSymbol}{parseFloat(positionForm.stop_loss).toLocaleString(isUsdAsset ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2})}</span>
                          </div>
                        )}
                        {positionForm.take_profit && (
                          <div>
                            <span className="text-gray-400">Take Profit:</span>
                            <span className="text-green-400 ml-2">{priceSymbol}{parseFloat(positionForm.take_profit).toLocaleString(isUsdAsset ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2})}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                  
                  <button onClick={editingPosition ? handleEditPosition : handleAddPosition} className="w-full mt-3 sm:mt-4 py-2 sm:py-3 bg-blue-600 text-sm sm:text-base hover:bg-blue-700 rounded-lg font-semibold">
                    {editingPosition ? '💾 Güncelle' : '➕ Ekle'}
                  </button>
                </div>
              )}
              
              {openPositions.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-6 sm:p-8 text-center text-gray-400 text-sm sm:text-base">Açık pozisyon yok</div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {openPositions.map(pos => {
                    // USD kategorileri - API'den gelen category veya asset_category kullan
                    const usdCategories = ['EMTIA', 'KRIPTO', 'ABD_NASDAQ', 'US_STOCKS', 'FOREX'];
                    const isUsdAsset = usdCategories.includes(pos.category) || usdCategories.includes(pos.asset_category);
                    const priceSymbol = isUsdAsset ? '$' : '₺';
                    
                    return (
                    <div key={pos.id} className="bg-gray-700 rounded-lg p-3 sm:p-4">
                      <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg sm:text-xl font-bold">{pos.asset_symbol}</span>
                            {isUsdAsset && <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">USD</span>}
                          </div>
                          <div className="text-xs text-gray-400">
                            Kaldıraç: {pos.leverage || 1}x
                            {pos.hidden_leverage && pos.hidden_leverage > 1 && (
                              <span className="ml-2 text-yellow-400 font-bold">| Gizli: {pos.hidden_leverage}x</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                          <button onClick={() => { setEditingPosition(pos); setPositionForm({ asset_id: pos.asset_id, amount: pos.amount.toString(), avg_buy_price: pos.avg_buy_price.toString(), leverage: pos.leverage.toString(), stop_loss: pos.stop_loss?.toString() || '', take_profit: pos.take_profit?.toString() || '' }); setShowAddPosition(false); }} className="px-2 sm:px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs sm:text-sm">📝</button>
                          <button 
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/close-position/${pos.id}`, {}, axiosConfig);
                                showAlert(`Pozisyon kapatıldı!\n\nKar/Zarar: ${formatCurrency(res.data.pnl, user)}\nYeni Bakiye: ${formatCurrency(res.data.new_balance, user)}`, 'success');
                                await fetchData();
                                if (onRefresh) await onRefresh();
                              } catch (err) {
                                showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                              }
                            }}
                            className="px-2 sm:px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs sm:text-sm"
                          >
                            Kapat
                          </button>
                          <button onClick={() => handleDeletePosition(pos.id, pos.asset_symbol)} className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs sm:text-sm">❌</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div><div className="text-gray-400">Miktar</div><div className="font-semibold">{pos.amount}</div></div>
                        <div><div className="text-gray-400">Ort. Alış</div><div className="font-semibold">{priceSymbol}{pos.avg_buy_price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Güncel</div><div className="font-semibold">{priceSymbol}{pos.current_price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Kar/Zarar</div><div className={`font-semibold ${pos.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.profit_loss >= 0 ? '+' : ''}₺{pos.profit_loss?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">%</div><div className={`font-semibold ${pos.profit_loss_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.profit_loss_percent >= 0 ? '+' : ''}{pos.profit_loss_percent?.toFixed(2)}%</div></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'closed' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold">Tamamlanan Pozisyonlar</h3>
                <button onClick={() => { 
                  setShowAddClosed(!showAddClosed); 
                  setEditingClosed(null); 
                  setClosedForm({ asset_id: '', amount: '', entry_price: '', exit_price: '', profit_loss: '', entry_date: new Date().toISOString().split('T')[0] });
                }} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold">
                  {showAddClosed ? '❌ İptal' : '➕ Tamamlanmış İşlem Ekle'}
                </button>
              </div>
              
              {showAddClosed && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h4 className="font-bold mb-4">{editingClosed ? '📝 Tamamlanmış İşlem Düzenle' : '➕ Tamamlanmış İşlem Ekle'}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Varlık *</label>
                      <select value={closedForm.asset_id} onChange={(e) => setClosedForm({...closedForm, asset_id: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base">
                        <option value="">Seçin</option>
                        {assets.map(a => (<option key={a.id} value={a.id}>{a.symbol} ({a.category})</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Miktar *</label>
                      <input type="number" value={closedForm.amount} onChange={(e) => setClosedForm({...closedForm, amount: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="100" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Giriş Fiyatı</label>
                      <input type="number" step="0.01" value={closedForm.entry_price} onChange={(e) => setClosedForm({...closedForm, entry_price: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="185.50" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Çıkış Fiyatı</label>
                      <input type="number" step="0.01" value={closedForm.exit_price} onChange={(e) => setClosedForm({...closedForm, exit_price: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="195.50" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Kar/Zarar (₺) *</label>
                      <input type="number" step="0.01" value={closedForm.profit_loss} onChange={(e) => setClosedForm({...closedForm, profit_loss: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="1000 veya -500" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Tarih</label>
                      <input type="date" value={closedForm.entry_date} onChange={(e) => setClosedForm({...closedForm, entry_date: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" />
                    </div>
                  </div>
                  <button onClick={handleAddClosed} className="w-full mt-3 sm:mt-4 py-2 sm:py-3 bg-blue-600 text-sm sm:text-base hover:bg-blue-700 rounded-lg font-semibold">
                    {editingClosed ? '💾 Güncelle' : '➕ Ekle'}
                  </button>
                </div>
              )}
              
              {closedTxs.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-8 text-center text-gray-400">Tamamlanmış işlem yok</div>
              ) : (
                <div className="space-y-3">
                  {closedTxs.slice(0, 30).map(tx => {
                    // Kapanma nedenini belirle
                    const getCloseReasonBadge = () => {
                      if (tx.transaction_type === 'CLOSE_STOPOUT' || tx.close_reason === 'MARGIN_STOPOUT') {
                        return <span className="ml-2 text-xs bg-red-600 px-2 py-0.5 rounded">STOP-OUT</span>;
                      }
                      if (tx.close_reason === 'SL_TRIGGERED' || tx.transaction_type === 'CLOSE_SL') {
                        return <span className="ml-2 text-xs bg-orange-600 px-2 py-0.5 rounded">SL</span>;
                      }
                      if (tx.close_reason === 'TP_TRIGGERED' || tx.transaction_type === 'CLOSE_TP') {
                        return <span className="ml-2 text-xs bg-green-600 px-2 py-0.5 rounded">TP</span>;
                      }
                      if (tx.close_reason === 'MANUAL' || tx.transaction_type === 'CLOSE') {
                        return <span className="ml-2 text-xs bg-blue-600 px-2 py-0.5 rounded">MANUEL</span>;
                      }
                      return null;
                    };
                    
                    return (
                    <div key={tx.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center">
                            <span className="font-bold">{tx.asset_symbol}</span>
                            {getCloseReasonBadge()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {tx.leverage && tx.leverage > 1 && (
                              <span>Kaldıraç: {tx.leverage}x</span>
                            )}
                            {tx.hidden_leverage && tx.hidden_leverage > 1 && (
                              <span className="ml-2 text-yellow-400 font-bold">| Gizli: {tx.hidden_leverage}x</span>
                            )}
                          </div>
                          <div className={`font-bold ${tx.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {tx.profit_loss >= 0 ? '+' : ''}₺{tx.profit_loss?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                          <button 
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/reopen-position/${tx.id}`, {}, axiosConfig);
                                showAlert(`Pozisyon yeniden açıldı!\n\nGiriş: ${formatCurrency(tx.entry_price || tx.price, user)}\nGüncel: ${formatCurrency(res.data.current_price, user)}`, 'success');
                                await fetchData();
                                if (onRefresh) await onRefresh();
                              } catch (err) {
                                showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                              }
                            }}
                            className="px-2 sm:px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs sm:text-sm"
                          >
                            🔄
                          </button>
                          <button 
                            onClick={() => {
                              setEditingClosed(tx);
                              setClosedForm({
                                asset_id: tx.asset_id,
                                amount: tx.amount.toString(),
                                entry_price: tx.entry_price?.toString() || tx.price?.toString() || '',
                                exit_price: tx.exit_price?.toString() || tx.price?.toString() || '',
                                profit_loss: tx.profit_loss.toString(),
                                entry_date: tx.timestamp ? new Date(tx.timestamp).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                              });
                              setShowAddClosed(true);
                            }}
                            className="px-2 sm:px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs sm:text-sm"
                          >
                            📝
                          </button>
                          <button 
                            onClick={() => handleDeleteClosed(tx.id)}
                            className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs sm:text-sm"
                          >
                            ❌
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><div className="text-gray-400">Miktar</div><div>{tx.amount}</div></div>
                        <div><div className="text-gray-400">Fiyat</div><div>₺{tx.price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Tarih</div><div className="text-xs">{new Date(tx.timestamp).toLocaleString('tr-TR')}</div></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'credit' && (
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4">💳 Kredi Yönetimi</h3>
                
                {/* Mevcut Kredi */}
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="text-gray-400 text-sm mb-1">Mevcut Kredi Bakiyesi</div>
                  <div className="text-2xl font-bold text-green-400">{formatCurrency(user.credit || 0, user)}</div>
                </div>
                
                {/* Kredi Ekle */}
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-3">➕ Kredi Ekle</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Tutar (₺)</label>
                      <input 
                        type="number" 
                        value={creditAmount} 
                        onChange={(e) => setCreditAmount(e.target.value)} 
                        placeholder="Örn: 1000" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Açıklama (opsiyonel)</label>
                      <input 
                        type="text" 
                        value={creditDescription} 
                        onChange={(e) => setCreditDescription(e.target.value)} 
                        placeholder="Örn: Promosyon kredisi" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <button 
                      onClick={async () => {
                        if (!creditAmount || parseFloat(creditAmount) <= 0) {
                          showAlert('Geçerli bir tutar girin', 'error');
                          return;
                        }
                        try {
                          const res = await axios.post(
                            `${API}/admin/users/${user.id}/credit/add?amount=${parseFloat(creditAmount)}&description=${encodeURIComponent(creditDescription || 'Admin kredi ekledi')}`,
                            {},
                            axiosConfig
                          );
                          showAlert(`Kredi başarıyla eklendi!\nYeni Kredi: ${formatCurrency(res.data.new_credit, user)}`, 'success');
                          setCreditAmount('');
                          setCreditDescription('');
                          onRefresh();
                        } catch (err) {
                          showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold"
                    >
                      ➕ Kredi Ekle
                    </button>
                  </div>
                </div>
                
                {/* Kredi Düş */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-red-400">➖ Kredi Düş</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Tutar (₺)</label>
                      <input 
                        type="number" 
                        value={creditAmount} 
                        onChange={(e) => setCreditAmount(e.target.value)} 
                        placeholder="Örn: 500" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <button 
                      onClick={async () => {
                        if (!creditAmount || parseFloat(creditAmount) <= 0) {
                          showAlert('Geçerli bir tutar girin', 'error');
                          return;
                        }
                        try {
                          const res = await axios.post(
                            `${API}/admin/users/${user.id}/credit/deduct?amount=${parseFloat(creditAmount)}&description=${encodeURIComponent(creditDescription || 'Admin kredi düşürdü')}`,
                            {},
                            axiosConfig
                          );
                          showAlert(`Kredi başarıyla düşürüldü!\nYeni Kredi: ${formatCurrency(res.data.new_credit, user)}`, 'success');
                          setCreditAmount('');
                          setCreditDescription('');
                          onRefresh();
                        } catch (err) {
                          showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                        }
                      }}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold"
                    >
                      ➖ Kredi Düş
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'password' && (
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">🔐 Şifre Yönetimi</h3>
              <div className="space-y-4">
                {/* Mevcut Şifre Gösterimi */}
                <div className="bg-gray-800 p-4 rounded-lg border border-green-600">
                  <label className="block text-xs sm:text-sm text-gray-400 mb-2">🔑 Mevcut Şifre</label>
                  <div className="flex items-center justify-between bg-gray-900 px-4 py-3 rounded-lg">
                    <div className="w-full">
                      <span className="text-white font-mono text-lg bg-green-900/30 px-4 py-2 rounded block text-center">
                        {user.plaintext_password || '(Kayıtlı şifre yok)'}
                      </span>
                      {!user.plaintext_password && (
                        <p className="text-yellow-400 text-xs mt-2 text-center">
                          ⚠️ Bu kullanıcı eski sistemden kalmış. Yeni şifre belirleyebilirsiniz.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Yeni Şifre */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
                  <label className="block text-xs sm:text-sm text-gray-400 mb-2">🔐 Yeni Şifre Belirle</label>
                  <input 
                    type="text" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    placeholder="Min 6 karakter" 
                    className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base mb-3" 
                  />
                  <button 
                    onClick={handleUpdatePassword} 
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold"
                  >
                    Şifreyi Güncelle
                  </button>
                  <p className="text-gray-500 text-xs mt-2 text-center">
                    Şifre güncellenince kullanıcı yeni şifre ile giriş yapabilir
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-bold">📜 Kullanıcı Aktivite Logları</h3>
                <button 
                  onClick={fetchActivityLogs} 
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
                >
                  🔄 Yenile
                </button>
              </div>
              
              {activityLoading ? (
                <div className="text-center py-8 text-gray-400">Yükleniyor...</div>
              ) : activityLogs.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-8 text-center">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-gray-400">Henüz aktivite kaydı yok</p>
                  <p className="text-gray-500 text-sm mt-1">Kullanıcı işlem yaptıkça burada görünecek</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {activityLogs.map((log, idx) => (
                    <div key={log.id || idx} className="bg-gray-700 rounded-lg p-3 hover:bg-gray-600 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-sm sm:text-base">{log.action}</div>
                          <div className="text-gray-300 text-xs sm:text-sm mt-1">{log.details}</div>
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <div className="mt-2 text-xs text-gray-400 bg-gray-800 rounded p-2">
                              {log.metadata.asset_symbol && <span className="inline-block mr-3">📊 {log.metadata.asset_symbol}</span>}
                              {log.metadata.amount && <span className="inline-block mr-3">📦 {log.metadata.amount} adet</span>}
                              {log.metadata.price && <span className="inline-block mr-3">💰 ₺{parseFloat(log.metadata.price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>}
                              {log.metadata.pnl !== undefined && (
                                <span className={`inline-block mr-3 ${log.metadata.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  K/Z: {log.metadata.pnl >= 0 ? '+' : ''}₺{parseFloat(log.metadata.pnl).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                                </span>
                              )}
                              {log.metadata.leverage && log.metadata.leverage > 1 && <span className="inline-block mr-3">⚡ {log.metadata.leverage}x</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap ml-3">
                          {new Date(log.timestamp).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {activityLogs.length > 0 && (
                <div className="text-center text-gray-500 text-xs mt-2">
                  Toplam {activityLogs.length} kayıt gösteriliyor
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailManagement;
